import React from 'react';
import {Box, Icon, ColumnLayout, SpaceBetween, Popover, Button, StatusIndicator} from '@amzn/awsui-components-react';
import {DashboardEvent} from '@amzn/f3-excelsior-orchestrator-api/clients/orchestrator';
import {ValueWithLabel} from '../common/value-with-label';
import {translateDurationToString} from '../orchestrator/view-schedule-event';

interface EventPopoverProps {
    event: DashboardEvent;
}

enum DashboardEventType {
    Generate,
    Publish,
    Promote,
    Ingest,
    PromoteAndPublish,
    CalculateAccuracy,
    PercentileForecast,
}

export const getDashEventType = (event: DashboardEvent): string => {
    if (event.generator) return DashboardEventType[DashboardEventType.Generate];
    if (event.publish) return DashboardEventType[DashboardEventType.Publish];
    if (event.promote) return DashboardEventType[DashboardEventType.Promote];
    if (event.ingestion) return DashboardEventType[DashboardEventType.Ingest];
    if (event.promoteAndPublish) return DashboardEventType[DashboardEventType.PromoteAndPublish];
    if (event.calculateAccuracy) return DashboardEventType[DashboardEventType.CalculateAccuracy];
    if (event.percentileForecast) return DashboardEventType[DashboardEventType.PercentileForecast];
    return 'Unknown';
};

export const generateEventSubtypePopoverContent = (event: DashboardEvent) => {
    if (event.generator) {
        return (
            <>
                <div>
                    <Box variant="strong">Generate Event</Box>
                </div>
                <ValueWithLabel label="Calculation Start Time">{event.generator.startTimeCalculation}</ValueWithLabel>
                <ValueWithLabel label="Forecast Type">{event.generator.forecastType}</ValueWithLabel>
                <ValueWithLabel label="Snapshot Date Override">
                    {event.generator.sopDateLocalOverride ? event.generator.sopDateLocalOverride : 'N/A'}
                </ValueWithLabel>
                <ValueWithLabel label="Model Group Name">
                    {event.generator.modelGroupName ? event.generator.modelGroupName : 'N/A'}
                </ValueWithLabel>
                <ValueWithLabel label="Model Api Version">
                    {event.generator.modelApiVersion ? event.generator.modelApiVersion : 'N/A'}
                </ValueWithLabel>
                <ValueWithLabel label="Model Config Override">
                    {event.generator.modelConfigOverride ? event.generator.modelConfigOverride : 'N/A'}
                </ValueWithLabel>
                <ValueWithLabel label="Last Published Model Run Id Override">
                    {event.generator.lastPublishedModelRunId ? event.generator.lastPublishedModelRunId : 'N/A'}
                </ValueWithLabel>
                <ValueWithLabel label="Workflow Input Override">
                    {event.generator.workflowInputOverride ? event.generator.workflowInputOverride : 'N/A'}
                </ValueWithLabel>
            </>
        );
    }
    if (event.publish) {
        return (
            <>
                <div>
                    <Box variant="strong">Publish Event</Box>
                </div>
                <ValueWithLabel label="Method">{event.publish.method}</ValueWithLabel>
                <ValueWithLabel label="Forecast Type">{event.publish.forecastType}</ValueWithLabel>
            </>
        );
    }
    if (event.ingestion) {
        return (
            <>
                <div>
                    <Box variant="strong">Ingest Event</Box>
                </div>
                <ValueWithLabel label="Data">{event.ingestion.data}</ValueWithLabel>
            </>
        );
    }
    if (event.promote) {
        return (
            <>
                <div>
                    <Box variant="strong">Promote Event</Box>
                </div>
            </>
        );
    }
    return <></>;
};

export const generatePopoverContent = (event: DashboardEvent) => {
    return (
        <ColumnLayout columns={2} variant="text-grid">
            <SpaceBetween size="l">
                <div>
                    <Box variant="awsui-key-label">Scheduled Time</Box>
                    <div>{event.scheduledTime.toLocaleString()}</div>
                </div>
                <div>
                    <Box variant="awsui-key-label">Original Scheduled Time</Box>
                    <div>{event.originalScheduledTime.toLocaleString()}</div>
                </div>
                <div>
                    <Box variant="awsui-key-label">Start Time</Box>
                    <div>{event.startTime ? event.startTime.toLocaleString() : 'N/A'}</div>
                </div>
                <div>
                    <Box variant="awsui-key-label">End Time</Box>
                    <div>{event.endTime ? event.endTime.toLocaleString() : 'N/A'}</div>
                </div>
                <div>
                    <Box variant="awsui-key-label">Status</Box>
                    <StatusIndicator data-testid="event-popover-status" type={getIndicatorType(event)}>
                        {event.status}
                    </StatusIndicator>
                </div>
                <div>
                    <Box variant="awsui-key-label">Is SLA Met</Box>
                    <div>{event.isSlaMet ? 'true' : 'false'}</div>
                </div>
                <div>
                    <Box variant="awsui-key-label">SLA</Box>
                    <div>{translateDurationToString(event.sla)}</div>
                </div>
            </SpaceBetween>
            <SpaceBetween size="l">{generateEventSubtypePopoverContent(event)}</SpaceBetween>
        </ColumnLayout>
    );
};

export default function EventPopover(props: EventPopoverProps) {
    const signForSLAMet = (event: DashboardEvent) => {
        return (
            <>
                <Button variant="link" wrapText={false}>
                    <StatusIndicator data-testid="event-row-status" type={getIndicatorType(props.event)}>
                        {`${event.scheduledTime.toLocaleTimeString()}${event.adhoc ? ' Adhoc ' : ' '}${getDashEventType(event)}`}
                    </StatusIndicator>
                </Button>
            </>
        );
    };

    return (
        <Box>
            <Popover
                dismissAriaLabel="Close"
                fixedWidth
                header={getDashEventType(props.event)}
                content={generatePopoverContent(props.event)}
                size="large"
                triggerType="custom"
            >
                {signForSLAMet(props.event)}
            </Popover>
        </Box>
    );
}

const getIndicatorType = (event: DashboardEvent): 'success' | 'error' | 'warning' | 'info' | 'loading' | 'pending' => {
    if (!event.isSlaMet) return 'warning';

    const statusMap = {
        succeed: 'success' as const,
        error: 'error' as const,
        running: 'loading' as const,
        not_started: 'pending' as const,
    };

    return statusMap[event.status as keyof typeof statusMap] || 'info';
};
